import React from 'react';
import styled from 'styled-components';

const ArtifactsPreview = ({ session }) => {
    if (!session) return null;

    // Check if there are any artifacts
    const hasArtifacts = session.prMeta ||
                        (session.files && session.files.length > 0) ||
                        (session.googleDocs && session.googleDocs.length > 0) ||
                        (session.figmaFiles && session.figmaFiles.length > 0);

    if (!hasArtifacts) return null;

    // Calculate total count
    const totalCount = (session.prMeta ? 1 : 0) +
                      (session.files?.length || 0) +
                      (session.googleDocs?.length || 0) +
                      (session.figmaFiles?.length || 0);

    return (
        <ArtifactsPreviewSection>
            <PreviewHeader>
                <PreviewTitle>Submitted Artifacts ({totalCount})</PreviewTitle>
            </PreviewHeader>
            <ArtifactsList>
                {session.prMeta && (
                    <ArtifactItem onClick={() => session.prLink && window.open(session.prLink, '_blank')}>
                        <ArtifactInfo>
                            <ArtifactTypeBadge $type="PR">PR</ArtifactTypeBadge>
                            <ArtifactName>{session.prMeta.title}</ArtifactName>
                        </ArtifactInfo>
                        <ViewButton>View →</ViewButton>
                    </ArtifactItem>
                )}
                {session.files?.map((file, index) => (
                    <ArtifactItem key={`file-${index}`} onClick={() => file.url && window.open(file.url, '_blank')}>
                        <ArtifactInfo>
                            <ArtifactTypeBadge $type="File">File</ArtifactTypeBadge>
                            <ArtifactName>{file.name}</ArtifactName>
                        </ArtifactInfo>
                        <ViewButton>View →</ViewButton>
                    </ArtifactItem>
                ))}
                {session.googleDocs?.map((doc, index) => {
                    const docUrl = doc.meta?.pdfStorage?.url || doc.link;
                    return (
                        <ArtifactItem key={`doc-${index}`} onClick={() => docUrl && window.open(docUrl, '_blank')}>
                            <ArtifactInfo>
                                <ArtifactTypeBadge $type="Google Doc">Google Doc</ArtifactTypeBadge>
                                <ArtifactName>{doc.name || doc.meta?.title || 'Untitled'}</ArtifactName>
                            </ArtifactInfo>
                            <ViewButton>View →</ViewButton>
                        </ArtifactItem>
                    );
                })}
                {session.figmaFiles?.map((figma, index) => {
                    const figmaUrl = figma.meta?.pdfStorage?.url || figma.link;
                    return (
                        <ArtifactItem key={`figma-${index}`} onClick={() => figmaUrl && window.open(figmaUrl, '_blank')}>
                            <ArtifactInfo>
                                <ArtifactTypeBadge $type="Figma">Figma</ArtifactTypeBadge>
                                <ArtifactName>{figma.name || figma.meta?.fileName || 'Untitled'}</ArtifactName>
                            </ArtifactInfo>
                            <ViewButton>View →</ViewButton>
                        </ArtifactItem>
                    );
                })}
            </ArtifactsList>
        </ArtifactsPreviewSection>
    );
};

const ArtifactsPreviewSection = styled.div`
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 28px;
`;

const PreviewHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const PreviewTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0;
`;

const ArtifactsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ArtifactItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px 16px;
    transition: all 0.2s;
    cursor: pointer;

    &:hover {
        border-color: #3b82f6;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
`;

const ArtifactInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ArtifactTypeBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    color: ${props => {
        switch(props.$type) {
            case 'PR': return '#1e40af';
            case 'File': return '#047857';
            case 'Google Doc': return '#92400e';
            case 'Figma': return '#7e22ce';
            default: return '#374151';
        }
    }};
    background-color: ${props => {
        switch(props.$type) {
            case 'PR': return '#dbeafe';
            case 'File': return '#d1fae5';
            case 'Google Doc': return '#fef3c7';
            case 'Figma': return '#f3e8ff';
            default: return '#f3f4f6';
        }
    }};
`;

const ArtifactName = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
`;

const ViewButton = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #3b82f6;
    padding: 6px 12px;
    border-radius: 6px;
    background-color: #eff6ff;
    transition: all 0.2s;
    white-space: nowrap;

    ${ArtifactItem}:hover & {
        background-color: #3b82f6;
        color: white;
    }
`;

export default ArtifactsPreview;
